import { type VariantProps, cva } from "class-variance-authority";
import { ReactNode } from "react";

// DESIGN.md → Status chips: a pill on its status set's flat subtle (100) fill, in the set's 700 text
// colour, as the SkillPanel badge has it.
// Blue is only for work still in progress; a state with no outcome to signal is neutral.
export const statusChipVariants = cva(
  "inline-flex h-5.5 items-center gap-1 whitespace-nowrap rounded-full px-2 text-xs font-semibold",
  {
    variants: {
      tone: {
        success: "bg-success-subtle text-success",
        warning: "bg-warning-subtle text-warning",
        error: "bg-destructive-subtle text-destructive-fg",
        progress: "bg-progress-subtle text-progress",
        neutral: "bg-emphasized text-neutral-fg",
      },
    },
  },
);

interface StatusChipProps extends VariantProps<typeof statusChipVariants> {
  children: ReactNode;
  /**
   * Announces a change of the chip's text to screen readers. Only for a chip that changes in
   * place after the admin's own action and has no other feedback; a tab full of live chips talks
   * over itself.
   */
  live?: boolean;
}

export const StatusChip = ({ tone, live = false, children }: StatusChipProps) => {
  return (
    <span
      role={live ? "status" : undefined}
      aria-live={live ? "polite" : undefined}
      className={statusChipVariants({ tone })}
    >
      {children}
    </span>
  );
};
